// apps/client/src/utils/dashboardFilters.ts
// Utility functions for dashboard filtering logic

import type { EnhancedDashboardFilters, MonthlyAggregate } from '../types';

/**
 * Calculate performance bands (percentiles) for brokers
 *
 * Ranks brokers by total commission (upfront + trail) and assigns them
 * to performance bands: top 20%, middle 60%, bottom 20%
 *
 * @param aggregates - Monthly aggregates with broker breakdowns
 * @returns Map of brokerDisplay -> performance band
 */
export function calculatePerformanceBands(
  aggregates: MonthlyAggregate[]
): Map<string, 'top20' | 'middle60' | 'bottom20'> {
  const performanceBands = new Map<string, 'top20' | 'middle60' | 'bottom20'>();

  // Calculate total commission per broker across all months
  const brokerTotals = new Map<string, number>();

  aggregates.forEach(agg => {
    if (agg.byBroker) {
      Object.entries(agg.byBroker).forEach(([brokerId, data]) => {
        const brokerDisplay = data.brokerDisplay || brokerId;
        const currentTotal = brokerTotals.get(brokerDisplay) || 0;
        // ✅ FIX: byBroker uses upfrontNet/trailNet, not upfrontCommission/trailCommission
        const upfront = (data as any).upfrontNet || data.upfrontCommission || 0;
        const trail = (data as any).trailNet || data.trailCommission || 0;
        const commission = upfront + trail;
        brokerTotals.set(brokerDisplay, currentTotal + commission);
      });
    }
  });

  // Sort brokers by total commission (descending)
  const sortedBrokers = Array.from(brokerTotals.entries())
    .sort((a, b) => b[1] - a[1]);

  const totalBrokers = sortedBrokers.length;
  const top20Count = Math.ceil(totalBrokers * 0.2);
  const bottom20Count = Math.ceil(totalBrokers * 0.2);

  // Assign performance bands
  sortedBrokers.forEach((entry, index) => {
    const [brokerDisplay] = entry;

    if (index < top20Count) {
      performanceBands.set(brokerDisplay, 'top20');
    } else if (index >= totalBrokers - bottom20Count) {
      performanceBands.set(brokerDisplay, 'bottom20');
    } else {
      performanceBands.set(brokerDisplay, 'middle60');
    }
  });

  return performanceBands;
}

/**
 * Filter aggregates based on enhanced dashboard filters
 *
 * Applies client-side filtering for:
 * - Broker filtering
 * - Commission type (upfront/trail/combined)
 * - Performance bands (top 20%/middle 60%/bottom 20%)
 *
 * Note: Lender filtering requires settlement data, not aggregates
 *
 * @param aggregates - Original monthly aggregates
 * @param filters - Enhanced dashboard filters
 * @param performanceBands - Pre-calculated performance bands (optional)
 * @returns Filtered aggregates
 */
export function filterAggregates(
  aggregates: MonthlyAggregate[],
  filters: EnhancedDashboardFilters,
  performanceBands?: Map<string, 'top20' | 'middle60' | 'bottom20'>
): MonthlyAggregate[] {
  return aggregates.map(agg => {
    let filteredAgg = { ...agg };

    // ✅ OFFICE DASHBOARD: Broker Filtering
    if (filters.broker && filters.broker !== 'all' && filteredAgg.byBroker) {
      const selectedBroker = filters.broker;
      let totalUpfront = 0;
      let totalTrail = 0;
      let totalLoanAmount = 0;
      let totalLineOfTrail = 0;
      let totalRecords = 0;

      // Find broker data across all broker IDs (handles normalized names)
      Object.entries(filteredAgg.byBroker).forEach(([brokerId, data]) => {
        const brokerDisplay = data.brokerDisplay || brokerId;
        if (brokerDisplay === selectedBroker) {
          totalUpfront += (data as any).upfrontNet || data.upfrontCommission || 0;
          totalTrail += (data as any).trailNet || data.trailCommission || 0;
          totalLoanAmount += data.totalLoanAmount || 0;
          totalLineOfTrail += data.lineOfTrail || 0;  // ✅ Now available in backend
          totalRecords += data.recordCount || 0;
        }
      });

      filteredAgg = {
        ...filteredAgg,
        upfrontCommission: totalUpfront,
        trailCommission: totalTrail,
        totalLoanAmount,
        lineOfTrail: totalLineOfTrail,  // ✅ Use broker-specific line of trail
        recordCount: totalRecords,
      };
    }

    // ✅ Commission Type Filtering
    if (filters.commissionType && filters.commissionType !== 'all') {
      if (filters.commissionType === 'upfront') {
        // Show only upfront commission
        filteredAgg = {
          ...filteredAgg,
          trailCommission: 0,
          byBroker: filteredAgg.byBroker ? Object.fromEntries(
            Object.entries(filteredAgg.byBroker).map(([id, data]) => [
              id,
              { ...data, trailCommission: 0 }
            ])
          ) : undefined,
        };
      } else if (filters.commissionType === 'trail') {
        // Show only trail commission
        filteredAgg = {
          ...filteredAgg,
          upfrontCommission: 0,
          byBroker: filteredAgg.byBroker ? Object.fromEntries(
            Object.entries(filteredAgg.byBroker).map(([id, data]) => [
              id,
              { ...data, upfrontCommission: 0 }
            ])
          ) : undefined,
        };
      }
      // 'combined' shows both (no filtering needed)
    }

    // ✅ Performance Band Filtering
    if (filters.performanceBand && filters.performanceBand !== 'all' && performanceBands && filteredAgg.byBroker) {
      const allowedBrokers = new Set<string>();

      // Find brokers in the selected performance band
      performanceBands.forEach((band, brokerDisplay) => {
        if (band === filters.performanceBand) {
          allowedBrokers.add(brokerDisplay);
        }
      });

      // Filter broker breakdown
      const filteredByBroker: typeof filteredAgg.byBroker = {};
      let totalUpfront = 0;
      let totalTrail = 0;
      let totalLoanAmount = 0;
      let totalRecords = 0;

      Object.entries(filteredAgg.byBroker).forEach(([brokerId, data]) => {
        const brokerDisplay = data.brokerDisplay || brokerId;
        if (allowedBrokers.has(brokerDisplay)) {
          filteredByBroker[brokerId] = data;
          // ✅ FIX: byBroker uses upfrontNet/trailNet, not upfrontCommission/trailCommission
          totalUpfront += (data as any).upfrontNet || data.upfrontCommission || 0;
          totalTrail += (data as any).trailNet || data.trailCommission || 0;
          totalLoanAmount += data.totalLoanAmount || 0;
          totalRecords += data.recordCount || 0;
        }
      });

      filteredAgg = {
        ...filteredAgg,
        byBroker: filteredByBroker,
        upfrontCommission: totalUpfront,
        trailCommission: totalTrail,
        totalLoanAmount,
        recordCount: totalRecords,
      };
    }

    return filteredAgg;
  });
}

/**
 * Calculate year-over-year change percentage
 *
 * @param currentValue - Current period value
 * @param previousValue - Previous year value
 * @returns Percentage change (e.g., 15.5 for 15.5% increase)
 */
export function calculateYoYChange(currentValue: number, previousValue: number): number {
  if (previousValue === 0) {
    return currentValue > 0 ? 100 : 0;
  }
  return ((currentValue - previousValue) / previousValue) * 100;
}

/**
 * Format YoY change for display
 *
 * @param change - Percentage change
 * @returns Formatted string (e.g., "+15.5%" or "-8.2%")
 */
export function formatYoYChange(change: number): string {
  const sign = change >= 0 ? '+' : '';
  return `${sign}${change.toFixed(1)}%`;
}

/**
 * Get trend indicator color
 *
 * @param change - Percentage change
 * @returns Tailwind color class
 */
export function getTrendColor(change: number): string {
  if (change > 0) return 'text-green-400';
  if (change < 0) return 'text-red-400';
  return 'text-gray-400';
}

/**
 * Get trend arrow icon
 *
 * @param change - Percentage change
 * @returns Unicode arrow character
 */
export function getTrendArrow(change: number): string {
  if (change > 5) return '↗'; // Significant increase
  if (change > 0) return '→'; // Slight increase
  if (change < -5) return '↘'; // Significant decrease
  if (change < 0) return '→'; // Slight decrease
  return '→'; // No change
}
